import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { directoryApi, type PartyContact } from '@my/directory-api';
import { partyContactKeys } from '@my/directory-api/hooks';

/**
 * Who an invoice email to this customer would reach.
 *
 * <p>
 * `unknown` is its own state, not "none": while the contacts load, or when the
 * lookup fails (no Parties:Read permission, Directory down), the grid must not
 * claim the customer HAS no billing contact. The server re-checks at send time
 * as the same user and answers with the precise reason, so an unknown state
 * leaves Send available rather than guessing.
 * </p>
 */
export type BillingRecipients =
  | { state: 'unknown' }
  | { state: 'none' }
  | { state: 'ok'; emails: string[] };

// Loose on purpose — the server (System.Net.Mail) is the authority, and this
// only has to agree with it on the obvious cases so the grid's warning and the
// confirmation's list match what a send will actually do.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;

/**
 * The same filter as `SendInvoiceEmailHandler.BillingRecipients`: contacts of
 * type Billing, trimmed, plausibly an address, de-duplicated ignoring case.
 */
export function billingEmailsOf(contacts: PartyContact[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of contacts) {
    if (c.type !== 'Billing') continue;
    const email = (c.email ?? '').trim();
    if (!EMAIL_SHAPE.test(email)) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

/**
 * Billing recipients per customer, keyed by party id, for every customer in
 * the list. Shares the Contacts screen's cache slots, so a billing contact
 * added there shows up here without a reload once the slot is refreshed.
 */
export function useBillingRecipients(partyIds: string[]): Map<string, BillingRecipients> {
  const ids = useMemo(() => [...new Set(partyIds)].sort(), [partyIds]);

  // `combine` rather than a useMemo over the results: TanStack re-runs it only
  // when a result or this function changes, so the map keeps its identity
  // between renders and rows don't re-render for nothing.
  const combine = useCallback(
    (results: { data?: PartyContact[] }[]) => {
      const map = new Map<string, BillingRecipients>();
      ids.forEach((id, index) => {
        const data = results[index]?.data;
        if (!data) {
          map.set(id, { state: 'unknown' });
          return;
        }
        const emails = billingEmailsOf(data);
        map.set(id, emails.length === 0 ? { state: 'none' } : { state: 'ok', emails });
      });
      return map;
    },
    [ids],
  );

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: partyContactKeys.byParty(id),
      queryFn: () => directoryApi.getPartyContacts(id),
      staleTime: 60 * 1000,
    })),
    combine,
  });
}
